import classNames from 'classnames';
import { motion } from 'framer-motion';
import './Drawer.scss';
import { commonTransition } from '../Header/Header.animations';
import { CloseIcon } from '@/icons';
import { FocusTrap } from 'focus-trap-react';
import { useEffect } from 'react';

type DrawerProps = {
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  scrollable?: boolean;
  offsetTop?: number;
  title?: string;
  narrow?: boolean;
  overlay?: boolean;
  onClose?: () => void;
};

const Drawer = ({
  children,
  className,
  scrollable,
  offsetTop,
  onClose,
  footer,
  title,
  narrow,
  overlay,
}: DrawerProps) => {
  // esc key to close drawer
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [onClose]);

  return (
    <>
      {overlay && (
        <motion.div
          transition={commonTransition}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="drawer__overlay"
        ></motion.div>
      )}

      <FocusTrap
        focusTrapOptions={{
          initialFocus: false,
          allowOutsideClick: true,
          returnFocusOnDeactivate: true,
        }}
      >
        <motion.div
          transition={commonTransition}
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          className={classNames('drawer', className, {
            'drawer--narrow': narrow,
          })}
          style={{
            top: offsetTop || 0,
            overflowY: scrollable ? 'auto' : 'hidden',
          }}
        >
          {(title || onClose) && (
            <div className="drawer__title">
              {title && <h2>{title}</h2>}
              {onClose && (
                <button type="button" onClick={() => onClose()}>
                  <CloseIcon />
                </button>
              )}
            </div>
          )}

          <motion.div
            transition={commonTransition}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {children}
          </motion.div>

          {footer && <div className="drawer__footer">{footer}</div>}
        </motion.div>
      </FocusTrap>
    </>
  );
};

export default Drawer;
